import { apiClient } from '@/lib/api-client';
import type {
  ActiveNetworkResponse,
  ActiveNetworkSummary,
  AuthUser,
  LoginResponse,
  RedesAcessiveisResponse,
} from '@just-frotas/shared';

// Wrappers de auth. Espelha apps/web/js/services.js (vanilla).
// Tipos vem de @just-frotas/shared (zod schemas).
export const authService = {
  login(email: string, senha: string): Promise<LoginResponse> {
    return apiClient.request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: { email, senha },
      auth: false,
    });
  },

  logout(): Promise<void> {
    return apiClient.request<void>('/auth/logout', { method: 'POST' });
  },

  // /auth/me retorna { user: AuthUser } (wrapper) - diferente de /auth/login
  // que retorna user no top level. Desempacotamos aqui pra normalizar.
  async me(): Promise<AuthUser> {
    const resp = await apiClient.request<{ user: AuthUser } | AuthUser>('/auth/me', {
      method: 'GET',
    });
    // Defesa: se backend mudar pra retornar user direto algum dia, ainda
    // funciona. Detecta o wrapper checando se tem .user que parece um AuthUser.
    if (
      resp &&
      typeof resp === 'object' &&
      'user' in resp &&
      resp.user &&
      typeof resp.user === 'object' &&
      'id' in resp.user
    ) {
      return resp.user as AuthUser;
    }
    return resp as AuthUser;
  },

  forgotPassword(email: string): Promise<{ ok: boolean; debugToken?: string }> {
    return apiClient.request('/auth/forgot-password', {
      method: 'POST',
      body: { email },
      auth: false,
    });
  },

  // O endpoint responde 204 No Content: nao existe corpo, entao tipar como
  // { ok: boolean } faria resp.ok ser undefined e ramificar nele trataria
  // sucesso como falha. Sucesso e a ausencia de excecao.
  resetPassword(token: string, novaSenha: string): Promise<void> {
    return apiClient.request('/auth/reset-password', {
      method: 'POST',
      body: { token, novaSenha },
      auth: false,
    });
  },

  changePassword(senhaAtual: string, novaSenha: string): Promise<{ ok: boolean }> {
    return apiClient.request('/auth/change-password', {
      method: 'POST',
      body: { senhaAtual, novaSenha },
    });
  },

  firstAccessChangePassword(novaSenha: string): Promise<LoginResponse> {
    return apiClient.request<LoginResponse>('/auth/first-access-change-password', {
      method: 'POST',
      body: { novaSenha },
    });
  },

  // Redes acessiveis (paralelo a /auth/clientes-acessiveis). Filtragem
  // por scope eh feita no backend. Suporta busca server-side.
  listRedesAcessiveis(busca?: string): Promise<{
    redes: ActiveNetworkSummary[];
    active: number | null;
  }> {
    const qs = busca ? `?busca=${encodeURIComponent(busca)}` : '';
    return apiClient.request<RedesAcessiveisResponse>(`/auth/redes-acessiveis${qs}`);
  },

  // Switch da rede ativa. Backend emite novo access token; caller deve
  // chamar setSession() com o token retornado e refreshMe() pra
  // atualizar contexto (activeContext.rede no /me reflete a nova).
  switchActiveNetwork(networkId: number): Promise<ActiveNetworkResponse> {
    return apiClient.request<ActiveNetworkResponse>('/auth/active-network', {
      method: 'POST',
      body: { networkId },
    });
  },
};
